import { Checklist, SerialEntity } from "models/Checklist";
import { OrderSerial } from "models/OrderSerial";
import { Product } from "models/Product";
import { formatDate } from "./formatDate";

export const serialEntityToProduct = (
  serialEntity: SerialEntity,
  checklist?: Checklist
): Product => {
  return {
    id: serialEntity.id,
    serial: serialEntity.serialNumber || "",
    item: serialEntity.model || "",
    description: serialEntity.maker || "",
    macAddressWired: serialEntity.macAddress || "",
    macAddressWifi: serialEntity.macAddressWifi || "",
    ip: serialEntity.ipAddress || "",
    ethernetPort: serialEntity.networkSocket || "",
    theftMarking: serialEntity.theftProtection || "",
    other: serialEntity.other || "",
    userName: serialEntity.username || "",
    password: serialEntity.password || "",
    // These fields can be derived from the checklist
    checklistId: checklist?.id || null,
    room: checklist?.roomName || null,
    orderNo: checklist?.partOfOrder || null,
    roomClassification: checklist?.categories || null,
    // SerialEntitys do not have these fields, so we set them to some default values
    parentId: null,
    children: [],
    rowNo: null,
    doaDate: null,
    assetNo: "",
    firmware: "",
    // SerialEntitys are created when they are installed
    installed: true,
    doa: false,
    // SerialEntitys are always existing products
    existing: true,
    // SerialEntitys are always editable because they are only created from the app
    editable: true,
  };
};

export const orderSerialToProduct = (orderSerial: OrderSerial, checklistId?: string): Product => {
  return {
    // Products are mostly based on OrderSerials
    id: orderSerial.id,
    serial: orderSerial.serial || "",
    parentId: orderSerial.parentId,
    checklistId: checklistId || null,
    item: orderSerial.item || "",
    description: orderSerial.description || "",
    macAddressWired: orderSerial.macAddressWired || "",
    macAddressWifi: orderSerial.macAddressWifi || "",
    ip: orderSerial.ip || "",
    ethernetPort: orderSerial.ethernetPort || "",
    theftMarking: orderSerial.theftMarking || "",
    other: orderSerial.other || "",
    userName: orderSerial.userName || "",
    password: orderSerial.password || "",
    room: orderSerial.room,
    orderNo: orderSerial.orderNo,
    roomClassification: orderSerial.roomClassification,
    rowNo: orderSerial.rowNo,
    doaDate: orderSerial.doaDate ? new Date(orderSerial.doaDate) : null,
    assetNo: orderSerial.assetNo || "",
    firmware: orderSerial.firmware || "",
    installed: orderSerial.installed,
    doa: orderSerial.doa,
    children: orderSerial.children.map((child) => orderSerialToProduct(child, checklistId)),
    editable: orderSerial.editable,
    // OrderSerials are never existing products
    existing: false,
  };
};

export const productToOrderSerial = (product: Product): OrderSerial => {
  return {
    id: product.id,
    parentId: product.parentId,
    rowNo: product.rowNo,
    serial: product.serial,
    doa: product.doa,
    doaDate: product.doaDate ? formatDate(product.doaDate) : null,
    other: product.other,
    description: product.description,
    password: product.password,
    macAddressWired: product.macAddressWired,
    macAddressWifi: product.macAddressWifi,
    ethernetPort: product.ethernetPort,
    assetNo: product.assetNo,
    ip: product.ip,
    theftMarking: product.theftMarking,
    userName: product.userName,
    firmware: product.firmware,
    item: product.item,
    installed: product.installed,
    room: product.room,
    orderNo: product.orderNo,
    roomClassification: product.roomClassification,
    children: [],
    editable: product.editable,
  };
};

export const productToSerialEntity = (product: Product): SerialEntity => {
  return {
    index: null,
    checklistId: product.checklistId || "",
    id: product.id,
    serialNumber: product.serial || null,
    maker: product.description || null,
    model: product.item || null,
    macAddress: product.macAddressWired || null,
    networkSocket: product.ethernetPort || null,
    other: product.other || null,
    noSerials: false,
    theftProtection: product.theftMarking || null,
    macAddressWifi: product.macAddressWifi || null,
    ipAddress: product.ip || null,
    username: product.userName || null,
    password: product.password || null,
  };
};
